/**
 * Sell Wizard — Custom Element
 *
 * Powers the multi-step condition wizard on the Sell page.
 * Flow: collection card button → dialog opens → 3 condition questions →
 *       recap with estimate → accept → contact form → Shopify contact form submit.
 *
 * Unlike the trade-in estimator, this writes nothing to a product form.
 * All data is compiled into the Shopify contact form body on submit.
 */

class SellWizard extends HTMLElement {
    /** @type {Record<string, { label: string, value: number }>} */
    #answers = {};

    /** Currently selected collection title */
    #collectionTitle = '';

    /** Base price from the selected product's custom.estimated_price metafield */
    #basePrice = 0;

    #promoValue = 0;
    #appliedPromoCode = '';

    // ---------------------------------------------------------------------------
    // Lifecycle
    // ---------------------------------------------------------------------------
  
    connectedCallback() {
      this.addEventListener('click', this.#handleClick);
      this.addEventListener('change', this.#handleChange);

      // Contact form: populate body field before submission
      const form = this.querySelector('.sell-wizard__form');
      form?.addEventListener('submit', this.#handleFormSubmit);

      // If the page reloaded after a successful submission, show the inline wizard with the success panel
      if (this.querySelector('[data-sell-success-panel]')) {
        this.querySelector('[data-sell-contact]')?.removeAttribute('hidden');
        this.#showInline();
      }
    }
  
    disconnectedCallback() {
      const form = this.querySelector('.sell-wizard__form');
      form?.removeEventListener('submit', this.#handleFormSubmit);
    }
  
    // ---------------------------------------------------------------------------
    // Event delegation
    // ---------------------------------------------------------------------------
  
    #handleChange = (/** @type {Event} */ event) => {
      const target = /** @type {Element} */ (event.target);
      if (target.matches('[data-sell-payment-type]')) {
        this.#updatePaymentMethod(/** @type {HTMLInputElement} */ (target).value);
      }
    };

    #handleClick = (/** @type {MouseEvent} */ event) => {
      const target = /** @type {Element} */ (event.target);

      const openBtn = target.closest('[data-sell-open]');
      if (openBtn instanceof HTMLElement) { this.#openDialog(openBtn); return; }

      const filterBtn = target.closest('[data-sell-filter]');
      if (filterBtn instanceof HTMLElement) { this.#activateFilter(filterBtn.dataset.sellFilter ?? ''); return; }

      const option = target.closest('[data-sell-option]');
      if (option instanceof HTMLElement) { this.#selectOption(option); return; }

      const continueBtn = target.closest('[data-sell-continue]');
      if (continueBtn) {
        const step = continueBtn.closest('[data-sell-step]');
        if (step instanceof HTMLElement) {
          const questionId = step.dataset.sellQuestion ?? '';
          if (!this.#answers[questionId]) return;
        }
        this.#advance(step);
        return;
      }

      if (target.closest('[data-sell-promo-apply]'))  { this.#applyPromo(); return; }
      if (target.closest('[data-sell-promo-remove]')) { this.#removePromo(); return; }
      if (target.closest('[data-sell-back]'))    { this.#goBack(); return; }
      if (target.closest('[data-sell-accept]'))  { this.#showContact(); return; }
      if (target.closest('[data-sell-restart]')) { this.#restart(); return; }
      if (target.closest('[data-sell-close]'))   { this.#closeDialog(); return; }
    };
  
    // ---------------------------------------------------------------------------
    // Dialog open / close
    // ---------------------------------------------------------------------------
  
    get #inline() {
      return this.querySelector('[data-sell-inline]');
    }

    get #collectionsEl() {
      return this.querySelector('.sell-page__collections');
    }

    #showInline() {
      this.#collectionsEl?.setAttribute('hidden', '');
      this.#inline?.removeAttribute('hidden');
      document.body.classList.add('sell-wizard-open');
      window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
    }

    /** @param {HTMLElement} btn */
    #openDialog(btn) {
      this.#collectionTitle = btn.dataset.sellCollectionTitle ?? '';
      const collectionHandle = btn.dataset.sellCollection ?? '';

      this.#restart(); // clears steps, options, and shows step 0

      // Seed the color before activateFilter so single-collection (no filter buttons) works too
      if (this.#inline instanceof HTMLElement && btn.dataset.sellColor) {
        this.#inline.style.setProperty('--collection-color', btn.dataset.sellColor);
      }

      this.#activateFilter(collectionHandle);

      const titleEl = this.querySelector('[data-sell-dialog-title]');
      if (titleEl) titleEl.textContent = this.#collectionTitle;

      this.#showInline();
    }

    /** @param {string} handle */
    #activateFilter(handle) {
      // Update filter button active states
      this.querySelectorAll('[data-sell-filter]').forEach((btn) => {
        if (!(btn instanceof HTMLElement)) return;
        const isActive = btn.dataset.sellFilter === handle;
        btn.classList.toggle('sell-wizard__filter-btn--active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      // Show the matching grid, hide the others; deselect options in hidden grids
      this.querySelectorAll('[data-sell-collection-group]').forEach((grid) => {
        if (!(grid instanceof HTMLElement)) return;
        const isActive = grid.dataset.sellCollectionGroup === handle;
        if (isActive) {
          grid.removeAttribute('hidden');
        } else {
          grid.setAttribute('hidden', '');
          grid.querySelectorAll('[data-sell-option]').forEach((opt) => {
            opt.classList.remove('sell-wizard__option--selected');
            opt.setAttribute('aria-checked', 'false');
          });
          delete this.#answers['product'];
        }
      });

      // Set collection color CSS variable for selected option styling
      const activeBtn = this.querySelector(`[data-sell-filter="${handle}"]`);
      if (this.#inline instanceof HTMLElement) {
        const color = activeBtn instanceof HTMLElement
          ? activeBtn.style.getPropertyValue('--collection-color')
          : '';
        this.#inline.style.setProperty('--collection-color', color || '');
      }

      // Hide continue button when switching filters (no product selected yet)
      const productStep = this.querySelector('[data-sell-step="0"]');
      productStep?.querySelector('[data-sell-continue]')?.setAttribute('hidden', '');
    }

    #closeDialog() {
      this.#inline?.setAttribute('hidden', '');
      this.#collectionsEl?.removeAttribute('hidden');
      document.body.classList.remove('sell-wizard-open');
      window.scrollTo({ top: this.getBoundingClientRect().top + window.scrollY, behavior: 'smooth' });
    }
  
    // ---------------------------------------------------------------------------
    // Step navigation
    // ---------------------------------------------------------------------------
  
    /** @param {HTMLElement} option */
    #selectOption(option) {
      const step       = option.closest('[data-sell-step]');
      const questionId = /** @type {HTMLElement} */ (step)?.dataset.sellQuestion ?? '';
      const label      = option.dataset.sellAnswer ?? '';
      const value      = parseInt(option.dataset.sellValue ?? '0', 10);
  
      const imgEl = option.querySelector('img');
      const imgSrc = imgEl?.currentSrc || imgEl?.src || null;
      const handle = option.dataset.sellProduct ?? null;
      this.#answers[questionId] = { label, value, imgSrc, ...(handle ? { handle } : {}) };

      step?.querySelectorAll('[data-sell-option]').forEach((opt) => {
        const selected = opt === option;
        opt.classList.toggle('sell-wizard__option--selected', selected);
        opt.setAttribute('aria-checked', selected ? 'true' : 'false');
      });

      // Load product-specific questions and base price when product is selected
      if (questionId === 'product') {
        this.#loadProductData(option.dataset.sellProduct ?? '');
      }

      // Show continue button instead of auto-advancing
      const continueBtn = /** @type {HTMLElement|null} */ (step?.querySelector('[data-sell-continue]'));
      if (continueBtn) continueBtn.removeAttribute('hidden');
    }

    // ---------------------------------------------------------------------------
    // Product data — questions and base price from metafield JSON
    // ---------------------------------------------------------------------------

    /** @param {string} handle */
    #loadProductData(handle) {
      try {
        const el   = this.querySelector(`[data-sell-product-data="${handle}"]`);
        const data = JSON.parse(el?.textContent ?? '{}');
        this.#basePrice = parseFloat(data.basePrice ?? 0);
        this.#renderDots(data.questions?.length ?? 0);
        this.#renderQuestionSteps(data.questions ?? []);
      } catch {
        this.#basePrice = 0;
        this.#renderDots(0);
        this.#renderQuestionSteps([]);
      }
    }

    /** @param {number} count */
    #renderDots(count) {
      const container = this.querySelector('[data-sell-progress]');
      if (!container) return;
      container.innerHTML = '';
      for (let i = 0; i < count; i++) {
        const dot = document.createElement('span');
        dot.className       = 'sell-wizard__dot';
        dot.dataset.sellDot = '';
        container.appendChild(dot);
      }
    }

    /** @param {{ name: string, description: string, options: { label: string, price: number }[] }[]} questions */
    #renderQuestionSteps(questions) {
      const container = this.querySelector('[data-sell-question-steps]');
      if (!container) return;
      container.innerHTML = '';

      questions.forEach((q, i) => {
        const step = document.createElement('div');
        step.className            = 'sell-wizard__step';
        step.dataset.sellStep     = String(i + 1);
        step.dataset.sellQuestion = `question-${i}`;
        step.setAttribute('hidden', '');

        // Header
        const header = document.createElement('div');
        header.className = 'sell-wizard__step-header';

        const count = document.createElement('span');
        count.className   = 'sell-wizard__step-count';
        count.textContent = `Domanda ${i + 1} di ${questions.length}`;
        header.appendChild(count);

        step.appendChild(header);

        // Scrollable content wrapper (question + options + desc)
        const scrollWrap = document.createElement('div');
        scrollWrap.className = 'sell-wizard__step-scroll';

        // Question text
        const questionEl = document.createElement('p');
        questionEl.className   = 'sell-wizard__question';
        questionEl.textContent = q.name;
        scrollWrap.appendChild(questionEl);

        // Options
        const optionsDiv = document.createElement('div');
        optionsDiv.className = 'sell-wizard__options';
        optionsDiv.setAttribute('role', 'radiogroup');
        optionsDiv.setAttribute('aria-label', q.name);

        q.options.forEach((opt) => {
          const btn = document.createElement('button');
          btn.type               = 'button';
          btn.className          = 'sell-wizard__option';
          btn.dataset.sellOption = '';
          btn.dataset.sellAnswer = opt.label;
          btn.dataset.sellValue  = String(opt.price);
          btn.setAttribute('role', 'radio');
          btn.setAttribute('aria-checked', 'false');

          const labelEl = document.createElement('span');
          labelEl.className   = 'sell-wizard__option-label';
          labelEl.textContent = opt.label;
          btn.appendChild(labelEl);

          optionsDiv.appendChild(btn);
        });

        scrollWrap.appendChild(optionsDiv);

        // Optional description — shown below options
        if (q.description) {
          const desc = document.createElement('p');
          desc.className   = 'sell-wizard__question-desc';
          desc.textContent = q.description;
          scrollWrap.appendChild(desc);
        }

        step.appendChild(scrollWrap);

        // Actions row: back (left) + continue (right)
        const actions = document.createElement('div');
        actions.className = 'sell-wizard__step-actions';

        const back = document.createElement('button');
        back.type             = 'button';
        back.className        = 'sell-wizard__back-btn button button--secondary';
        back.dataset.sellBack = '';
        back.textContent      = '← Indietro';
        actions.appendChild(back);

        const continueBtn = document.createElement('button');
        continueBtn.type                  = 'button';
        continueBtn.className             = 'button sell-wizard__continue-btn';
        continueBtn.dataset.sellContinue  = '';
        continueBtn.setAttribute('hidden', '');
        continueBtn.textContent           = 'Continua';
        actions.appendChild(continueBtn);

        step.appendChild(actions);

        container.appendChild(step);
      });
    }

    /** @param {Element | null} currentStep */
    #advance(currentStep) {
      if (!(currentStep instanceof HTMLElement)) return;
      const steps     = Array.from(this.querySelectorAll('[data-sell-step]'));
      const stepIndex = steps.indexOf(currentStep);
  
      currentStep.setAttribute('hidden', '');
  
      if (stepIndex + 1 < steps.length) {
        this.#showStep(stepIndex + 1);
      } else {
        this.#showRecap();
      }
    }
  
    #goBack() {
      // Determine which panel is currently visible
      const recapEl   = this.querySelector('[data-sell-recap]');
      const contactEl = this.querySelector('[data-sell-contact]');
  
      if (recapEl && !recapEl.hasAttribute('hidden')) {
        // Back from recap → last question step
        recapEl.setAttribute('hidden', '');
        const steps = Array.from(this.querySelectorAll('[data-sell-step]'));
        this.#showStep(steps.length - 1);
        return;
      }
  
      if (contactEl && !contactEl.hasAttribute('hidden')) {
        // Back from contact form → recap
        contactEl.setAttribute('hidden', '');
        this.#showRecap();
        return;
      }
  
      // Back within steps
      const steps        = Array.from(this.querySelectorAll('[data-sell-step]'));
      const currentIndex = steps.findIndex((s) => !s.hasAttribute('hidden'));
      if (currentIndex <= 0) return;
  
      steps[currentIndex].setAttribute('hidden', '');
      this.#showStep(currentIndex - 1);
    }
  
    /** @param {number} index */
    #showStep(index) {
      const steps = Array.from(this.querySelectorAll('[data-sell-step]'));
      const step  = steps[index];
      if (!(step instanceof HTMLElement)) return;
  
      step.removeAttribute('hidden');
      this.querySelector('[data-sell-progress]')?.removeAttribute('hidden');

      // Dots only track condition steps — offset past the product step
      const firstConditionIndex = steps.findIndex((s) => s.dataset.sellQuestion !== 'product');
      const dotIndex = (firstConditionIndex === -1 || index < firstConditionIndex)
        ? -2
        : index - firstConditionIndex;
      this.#updateDots(dotIndex);
  
      // Restore previous selection when navigating back
      const questionId = step.dataset.sellQuestion ?? '';
      const previous   = this.#answers[questionId];
      const continueBtn = /** @type {HTMLElement|null} */ (step.querySelector('[data-sell-continue]'));
      if (previous) {
        step.querySelectorAll('[data-sell-option]').forEach((opt) => {
          if (!(opt instanceof HTMLElement)) return;
          const selected = opt.dataset.sellAnswer === previous.label;
          opt.classList.toggle('sell-wizard__option--selected', selected);
          opt.setAttribute('aria-checked', selected ? 'true' : 'false');
        });
        if (continueBtn) continueBtn.removeAttribute('hidden');
      } else {
        if (continueBtn) continueBtn.setAttribute('hidden', '');
      }
    }
  
    // ---------------------------------------------------------------------------
    // Recap
    // ---------------------------------------------------------------------------
  
    #showRecap() {
      const recap = this.querySelector('[data-sell-recap]');
      if (!(recap instanceof HTMLElement)) return;

      recap.removeAttribute('hidden');
      this.querySelector('[data-sell-progress]')?.setAttribute('hidden', '');
      this.#updateDots(-1); // All done

      // Build summary card: product image row + condition answers dropdown
      const summaryEl = recap.querySelector('[data-sell-summary]');
      if (summaryEl) {
        summaryEl.innerHTML = '';

        let productRow = null;
        const conditionRows = [];

        this.querySelectorAll('[data-sell-step]').forEach((step) => {
          if (!(step instanceof HTMLElement)) return;
          const qId    = step.dataset.sellQuestion ?? '';
          const answer = this.#answers[qId];
          if (!answer) return;

          if (qId === 'product') {
            // Product hero: image + title
            productRow = document.createElement('div');
            productRow.className = 'sell-wizard__summary-product';

            if (answer.imgSrc) {
              const img = document.createElement('img');
              img.src       = answer.imgSrc;
              img.alt       = answer.label;
              img.className = 'sell-wizard__summary-product-img';
              productRow.appendChild(img);
            }

            const nameEl = document.createElement('span');
            nameEl.className   = 'sell-wizard__summary-product-name';
            nameEl.textContent = answer.label;
            productRow.appendChild(nameEl);
          } else {
            const row = document.createElement('div');
            row.className = 'sell-wizard__summary-row';

            const question = step.querySelector('.sell-wizard__question')?.textContent?.trim() ?? qId;

            const labelEl = document.createElement('span');
            labelEl.className   = 'sell-wizard__summary-row-label';
            labelEl.textContent = question;

            const valueEl = document.createElement('span');
            valueEl.className   = 'sell-wizard__summary-row-value';
            valueEl.textContent = answer.label;

            row.appendChild(labelEl);
            row.appendChild(valueEl);
            conditionRows.push(row);
          }
        });

        if (productRow) summaryEl.appendChild(productRow);

        // Wrap condition rows in a <details> dropdown
        if (conditionRows.length) {
          const details = document.createElement('details');
          details.className = 'sell-wizard__summary-details';

          const summary = document.createElement('summary');
          summary.className   = 'sell-wizard__summary-toggle';
          summary.textContent = 'Vedi dettagli';
          details.appendChild(summary);

          conditionRows.forEach((row) => details.appendChild(row));
          summaryEl.appendChild(details);
        }
      }

      const estimateEl = recap.querySelector('[data-sell-estimate]');
      if (estimateEl) estimateEl.textContent = this.#formatMoney(this.#calculateEstimate());
    }
  
    // ---------------------------------------------------------------------------
    // Contact form
    // ---------------------------------------------------------------------------
  
    /** @param {'paypal'|'iban'} type */
    #updatePaymentMethod(type) {
      const descEl = this.querySelector('[data-sell-payment-desc]');
      const input  = this.querySelector('[data-sell-payment-input]');

      if (descEl) {
        descEl.textContent = type === 'iban'
          ? 'Inserisci il tuo IBAN. Ti invieremo il rimborso tramite bonifico entro 5 giorni lavorativi dalla ricezione della console.'
          : 'Inserisci la tua email PayPal. Ti invieremo il rimborso entro 3 giorni lavorativi dalla ricezione della console.';
      }

      if (input instanceof HTMLInputElement) {
        input.placeholder = type === 'iban' ? 'IT60 X054 2811 1010 0000 0123 456' : 'email@esempio.it';
        input.value = '';
      }
    }

    #showContact() {
      this.querySelector('[data-sell-recap]')?.setAttribute('hidden', '');
      const contact = this.querySelector('[data-sell-contact]');
      contact?.removeAttribute('hidden');

      const checkedType = this.querySelector('[data-sell-payment-type]:checked')?.value ?? 'paypal';
      this.#updatePaymentMethod(checkedType);
    }

    #applyPromo() {
      const input     = this.querySelector('[data-sell-promo-input]');
      const errorEl   = this.querySelector('[data-sell-promo-error]');
      const successEl = this.querySelector('[data-sell-promo-success]');
      const code      = input instanceof HTMLInputElement ? input.value.trim().toUpperCase() : '';

      const handle = this.#answers['product']?.handle ?? '';
      let promoCodes = [];
      try {
        const el = this.querySelector(`[data-sell-product-data="${handle}"]`);
        const data = JSON.parse(el?.textContent ?? '{}');
        promoCodes = data.promoCodes ?? [];
      } catch { /* malformed JSON */ }

      const match = promoCodes.find((p) => (p.codice ?? '').toUpperCase() === code);
      if (!match) {
        errorEl?.removeAttribute('hidden');
        return;
      }

      errorEl?.setAttribute('hidden', '');
      this.#promoValue       = parseInt(match.valore ?? '0', 10);
      this.#appliedPromoCode = match.codice;

      const estimateEl = this.querySelector('[data-sell-recap] [data-sell-estimate]');
      if (estimateEl) estimateEl.textContent = this.#formatMoney(this.#calculateEstimate());

      if (successEl) {
        successEl.textContent = `Codice "${match.codice}" applicato: +${this.#formatMoney(this.#promoValue)}`;
        successEl.removeAttribute('hidden');
      }
      if (input instanceof HTMLInputElement) input.disabled = true;
      this.querySelector('[data-sell-promo-apply]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-promo-remove]')?.removeAttribute('hidden');
    }

    #removePromo() {
      this.#promoValue       = 0;
      this.#appliedPromoCode = '';

      const input = this.querySelector('[data-sell-promo-input]');
      if (input instanceof HTMLInputElement) { input.value = ''; input.disabled = false; }
      this.querySelector('[data-sell-promo-error]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-promo-success]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-promo-apply]')?.removeAttribute('hidden');
      this.querySelector('[data-sell-promo-remove]')?.setAttribute('hidden', '');

      const estimateEl = this.querySelector('[data-sell-recap] [data-sell-estimate]');
      if (estimateEl) estimateEl.textContent = this.#formatMoney(this.#calculateEstimate());
    }

    #handleFormSubmit = (/** @type {SubmitEvent} */ event) => {
      const requiredAddressFields = /** @type {HTMLInputElement[]} */ (
        Array.from(this.querySelectorAll('[data-sell-address-field]'))
      );
      const firstEmpty = requiredAddressFields.find((el) => !el.value.trim());
      if (firstEmpty) {
        event.preventDefault();
        firstEmpty.focus();
        firstEmpty.reportValidity();
        return;
      }

      const bodyField = this.querySelector('[data-sell-contact-body]');
      if (bodyField instanceof HTMLInputElement) {
        bodyField.value = this.#buildEmailBody();
      }
    };
  
    #buildEmailBody() {
      const lines = [
        `Richiesta di vendita — ${this.#collectionTitle}`,
        '',
        '--- Condizioni Console ---',
      ];
  
      this.querySelectorAll('[data-sell-step]').forEach((step) => {
        if (!(step instanceof HTMLElement)) return;
        const qId    = step.dataset.sellQuestion ?? '';
        const answer = this.#answers[qId];
        if (!answer) return;
        const question = step.querySelector('.sell-wizard__question')?.textContent?.trim() ?? qId;
        lines.push(`${question}: ${answer.label}`);
      });
  
      lines.push('', `Valore stimato: ${this.#formatMoney(this.#calculateEstimate())}`);
      if (this.#appliedPromoCode) {
        lines.push(`Codice promo: ${this.#appliedPromoCode} (+${this.#formatMoney(this.#promoValue)})`);
      }

      // Address fields (not submitted as contact[] — compiled into body here)
      const addressParts = [];
      this.querySelectorAll('[data-sell-address-field]').forEach((el) => {
        if (!(el instanceof HTMLInputElement)) return;
        const val = el.value.trim();
        if (val) addressParts.push(val);
      });
  
      if (addressParts.length > 0) {
        lines.push('', '--- Indirizzo ---', addressParts.join(', '));
      }
  
      return lines.join('\n');
    }
  
    // ---------------------------------------------------------------------------
    // Reset
    // ---------------------------------------------------------------------------
  
    #restart() {
      this.#answers          = {};
      this.#basePrice        = 0;
      this.#promoValue       = 0;
      this.#appliedPromoCode = '';
      const promoInput = this.querySelector('[data-sell-promo-input]');
      if (promoInput instanceof HTMLInputElement) { promoInput.value = ''; promoInput.disabled = false; }
      this.querySelector('[data-sell-promo-error]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-promo-success]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-promo-apply]')?.removeAttribute('hidden');
      this.querySelector('[data-sell-promo-remove]')?.setAttribute('hidden', '');

      this.querySelectorAll('[data-sell-step]').forEach((s) => s.setAttribute('hidden', ''));
      this.querySelector('[data-sell-recap]')?.setAttribute('hidden', '');
      this.querySelector('[data-sell-contact]')?.setAttribute('hidden', '');
      this.querySelectorAll('[data-sell-option]').forEach((opt) => {
        opt.classList.remove('sell-wizard__option--selected');
        opt.setAttribute('aria-checked', 'false');
      });

      // Clear dynamically rendered question steps and dots
      const questionSteps = this.querySelector('[data-sell-question-steps]');
      if (questionSteps) questionSteps.innerHTML = '';

      const progress = this.querySelector('[data-sell-progress]');
      if (progress) progress.innerHTML = '';

      // Clear contact form inputs and reset payment type to PayPal
      this.querySelector('.sell-wizard__form')?.reset();
      const paypalRadio = this.querySelector('[data-sell-payment-type][value="paypal"]');
      if (paypalRadio instanceof HTMLInputElement) paypalRadio.checked = true;

      this.#showStep(0);
    }
  
    // ---------------------------------------------------------------------------
    // Helpers
    // ---------------------------------------------------------------------------
  
    #calculateEstimate() {
      const adjustments = Object.values(this.#answers).reduce((sum, { value }) => sum + value, 0);
      return Math.max(0, this.#basePrice + adjustments + this.#promoValue);
    }

    /** @param {number} amount */
    #formatMoney(amount) {
      const format        = this.dataset.currencyFormat ?? '{{amount}}';
      const formatted     = amount.toFixed(2);
      const amountRounded = Math.round(amount).toString();

      return format
        .replace('{{amount}}',                                  formatted)
        .replace('{{amount_no_decimals}}',                      amountRounded)
        .replace('{{amount_with_comma_separator}}',             formatted.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', amountRounded);
    }
  
    /**
     * @param {number} dotIndex
     *   -2 = product step (no dots lit)
     *   -1 = all done
     *   0+ = zero-based active condition step index
     */
    #updateDots(dotIndex) {
      this.querySelectorAll('[data-sell-dot]').forEach((dot, i) => {
        const isPreCondition = dotIndex === -2;
        dot.classList.toggle('sell-wizard__dot--active', !isPreCondition && i === dotIndex);
        dot.classList.toggle('sell-wizard__dot--done',   !isPreCondition && (dotIndex === -1 || i < dotIndex));
      });
    }
  }
  
  if (!customElements.get('sell-wizard')) {
    customElements.define('sell-wizard', SellWizard);
  }
  